import {
  EnrollmentEffortTier,
  MedicalPlanForEnrollment,
  NetworkType,
  PlanMarket,
  SelfEnrollType,
  TierLevel,
} from '@zorro/clients';
import { isDefined } from '@zorro/common-utils';
import { YesNo } from '@zorro/types';
import { $enum } from 'ts-enum-util';

export enum Eligibility {
  ELIGIBLE = 'Eligible',
  NON_ELIGIBLE = 'Non-eligible',
}

export enum FilterGroups {
  LEVEL = 'level',
  PLAN_TYPE = 'planType',
  PLAN_MARKET = 'planMarket',
  HSA_ELIGIBLE = 'hsaEligible',
  CARRIER_NAME = 'carrierName',
  SELF_ENROLL = 'selfEnroll',
  ENROLLMENT_EFFORT = 'enrollmentEffortTier',
}

type FilterGroup = {
  [FilterGroups.LEVEL]: TierLevel;
  [FilterGroups.PLAN_TYPE]: NetworkType;
  [FilterGroups.PLAN_MARKET]: PlanMarket;
  [FilterGroups.HSA_ELIGIBLE]: Eligibility;
  [FilterGroups.CARRIER_NAME]: string;
  [FilterGroups.SELF_ENROLL]: YesNo;
  [FilterGroups.ENROLLMENT_EFFORT]: EnrollmentEffortTier;
};

export type UniqueValueFilterGroup = {
  [K in keyof FilterGroup]: Set<FilterGroup[K]>;
};
export type FilterGroupMap = {
  [K in keyof FilterGroup]: FilterGroup[K][];
};

export const initialFilterGroupMap = $enum(FilterGroups)
  .getValues()
  .reduce((acc, next) => ({ ...acc, [next]: [] }), {} as FilterGroupMap);

type BuildFiltersPlanType = Pick<
  MedicalPlanForEnrollment,
  | 'level'
  | 'planType'
  | 'planMarket'
  | 'hsaEligible'
  | 'carrierName'
  | 'logoUrl'
  | 'selfEnroll'
  | 'enrollmentEffortTier'
>;

const filterGroupValues = $enum(FilterGroups).getValues();

export const mapHsaEligibility = (hsaEligible: boolean): Eligibility => {
  return hsaEligible ? Eligibility.ELIGIBLE : Eligibility.NON_ELIGIBLE;
};

/* eslint-disable sonarjs/cognitive-complexity */
export const mapMedicalPlanToFilterGroups = (
  plans?: BuildFiltersPlanType[],
  { isEnrollmentEffortOn = false } = {}
) => {
  const uniqueValuesPerGroup: { [key: string]: Set<unknown> } = {};
  const mapCarrierNameToLogoUrl = new Map<string, string>();

  if (plans) {
    filterGroupValues.forEach((key) => {
      uniqueValuesPerGroup[key] = new Set<unknown>();
    });

    plans?.forEach((plan) => {
      filterGroupValues.forEach((key) => {
        if (key in plan) {
          if (key === FilterGroups.CARRIER_NAME) {
            uniqueValuesPerGroup[key]?.add(plan[key]);
            // assuming each carrierName has a unique logoUrl (1 : 1)
            if (plan.logoUrl) {
              mapCarrierNameToLogoUrl.set(plan[key], plan.logoUrl);
            }
          } else if (key === FilterGroups.HSA_ELIGIBLE) {
            const value = mapHsaEligibility(plan[key]);
            uniqueValuesPerGroup[key]?.add(value);
          } else if (key === FilterGroups.ENROLLMENT_EFFORT) {
            if (
              isEnrollmentEffortOn &&
              isDefined(plan[key]) &&
              plan[key] !== EnrollmentEffortTier.UNKNOWN
            ) {
              uniqueValuesPerGroup[key]?.add(plan[key]);
            }
          } else if (key === FilterGroups.SELF_ENROLL) {
            // Classified plans filter by enrollment effort tier instead, unless the flag is off
            if (isEnrollmentEffortOn && isDefined(plan.enrollmentEffortTier)) {
              return;
            }
            const value =
              plan[key] === SelfEnrollType.SELF_ENROLL ? YesNo.YES : YesNo.NO;
            uniqueValuesPerGroup[key]?.add(value);
          } else if (key === FilterGroups.LEVEL) {
            // Special handling for LEVEL to map expanded_bronze to bronze
            if (plan[key] === 'expanded_bronze') {
              // We don't add expanded_bronze directly to the filter options
              // but we will check for it in the filter logic
              uniqueValuesPerGroup[key]?.add('bronze');
            } else if (plan[key] !== undefined) {
              uniqueValuesPerGroup[key]?.add(plan[key]);
            }
          } else if (plan[key] !== undefined) {
            uniqueValuesPerGroup[key]?.add(plan[key]);
          }
        }
      });
    });
  }

  return { uniqueValuesPerGroup, mapCarrierNameToLogoUrl } as {
    uniqueValuesPerGroup: UniqueValueFilterGroup;
    mapCarrierNameToLogoUrl: Map<string, string>;
  };
};

export const getActivePlanFilterCount = (planFilters: FilterGroupMap) => {
  return Object.entries(planFilters).reduce(
    (acc, [filterGroup, activateFilterInGroup]) => {
      if (
        filterGroup === FilterGroups.LEVEL &&
        (activateFilterInGroup as TierLevel[]).includes(
          TierLevel.expanded_bronze
        )
      ) {
        return acc + (activateFilterInGroup.length - 1);
      }
      return acc + activateFilterInGroup.length;
    },
    0
  );
};
